import { ApiResponseType, jsonInit, toSearchParams } from '@shigo-ui/api-client';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import {
  EndpointIntegrationType,
  EndpointLookupStatus,
  EndpointPlacementType,
  endpointLookupPageSchema,
  endpointMacrosSchema,
  generatedEndpointSchema,
  type EndpointAdServerType,
  type EndpointGeneratorRequest,
} from './model';

interface EndpointGeneratorClientOptions {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
}

interface EndpointLookupRequest {
  searchTerm: string;
  selectedId?: string | undefined;
}

interface PlacementLookupRequest extends EndpointLookupRequest {
  publisherId?: string | undefined;
}

export function createEndpointGeneratorClient({
  config,
  accessToken,
  fetcher,
}: EndpointGeneratorClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);

  return {
    listPublishers: ({ searchTerm, selectedId }: EndpointLookupRequest) =>
      api.request({
        path: `/api/publishers?${toSearchParams({
          page: 0,
          size: 25,
          name: searchTerm || undefined,
          ids: selectedId || undefined,
          status: EndpointLookupStatus.Active,
        })}`,
        schema: endpointLookupPageSchema,
      }),
    listPlacements: ({ searchTerm, selectedId, publisherId }: PlacementLookupRequest) =>
      api.request({
        path: `/api/placements?${toSearchParams({
          page: 0,
          size: 25,
          searchTerm: searchTerm || undefined,
          ids: selectedId || undefined,
          publishersIds: publisherId || undefined,
          status: EndpointLookupStatus.Active,
          type: EndpointPlacementType.Standard,
        })}`,
        schema: endpointLookupPageSchema,
      }),
    listMacros: (adServerType: EndpointAdServerType) =>
      api.request({
        path: `/api/endpoint-generator/macros-ad-server?${toSearchParams({
          adServer: adServerType,
        })}`,
        schema: endpointMacrosSchema,
      }),
    listExchangeMacros: () =>
      api.request({
        path: '/api/endpoint-generator/macros-exchange',
        schema: endpointMacrosSchema,
      }),
    generateExchangeConnection: (endpoint: string, source: string) =>
      api.request({
        path: '/api/endpoint-generator/exchange-connection-generator',
        schema: generatedEndpointSchema,
        responseType: ApiResponseType.Text,
        init: jsonInit('POST', { endpoint, source }),
      }),
    generate: (integrationType: EndpointIntegrationType, payload: EndpointGeneratorRequest) =>
      api.request({
        path:
          integrationType === EndpointIntegrationType.Ortb
            ? '/api/endpoint-generator/ortb-tag-generator'
            : '/api/endpoint-generator/exchange-tag-generator',
        schema: generatedEndpointSchema,
        responseType: ApiResponseType.Text,
        init: jsonInit('POST', payload),
      }),
  };
}
